// src/features/auth/reset-password/pages/ResetPasswordPage.tsx

import React, {
  useState,
} from 'react';

import {
  ArrowLeft,
  CheckCircle2,
  KeyRound,
} from 'lucide-react';

import {
  useNavigate,
} from 'react-router-dom';

import RouteConfig
  from '../../../../routes/RouteConfig';

import {
  useModal,
} from '../../../../shared/components/ModalProvider';

import {
  usePhoneVerification,
} from '../../phone-verification/hooks/usePhoneVerification';

import {
  resetPassword,
} from '../services/resetPasswordService';

import {
  getErrorMessage,
  normalizePhoneNumber,
} from '../../signup/utils/signup.utils';


const ResetPasswordPage:
  React.FC = () => {
  const navigate =
    useNavigate();

  const modal =
    useModal();


  const [
    userId,
    setUserId,
  ] =
    useState('');


  const [
    phoneNumber,
    setPhoneNumber,
  ] =
    useState('');


  const [
    newPassword,
    setNewPassword,
  ] =
    useState('');


  const [
    newPasswordConfirm,
    setNewPasswordConfirm,
  ] =
    useState('');


  const [
    resetting,
    setResetting,
  ] =
    useState(false);


  const [
    resetCompleted,
    setResetCompleted,
  ] =
    useState(false);


  /**
   * =========================================================
   * 비밀번호 재설정용 전화번호 인증
   * =========================================================
   */
  const phoneVerification =
    usePhoneVerification({
      phoneNumber,

      purpose:
        'RESET_PASSWORD',
    });


  /**
   * =========================================================
   * 전화번호 변경
   * =========================================================
   */
  const handlePhoneNumberChange = (
    value: string,
  ): void => {
    const normalized =
      value
        .replace(
          /\D/g,
          '',
        )
        .slice(
          0,
          11,
        );


    /**
     * 전화번호가 변경되면
     * 기존 인증번호 / 인증토큰을 무효화합니다.
     */
    phoneVerification
      .resetVerification();


    setResetCompleted(
      false,
    );


    setPhoneNumber(
      normalized,
    );
  };


  /**
   * =========================================================
   * 아이디 변경
   *
   * 인증 완료 후에는
   * 인증 대상 아이디를 변경하지 못하도록 합니다.
   * =========================================================
   */
  const handleUserIdChange = (
    value: string,
  ): void => {
    if (
      phoneVerification
        .phoneVerified
    ) {
      return;
    }


    setUserId(
      value,
    );
  };


  /**
   * =========================================================
   * 새 비밀번호 입력값 검증
   *
   * 주의:
   * 기존 비밀번호와 동일한지는
   * 프론트에서 확인할 수 없습니다.
   *
   * 최종 동일 여부는 백엔드가
   * PasswordEncoder.matches()로 검증해야 합니다.
   * =========================================================
   */
  const validatePassword =
    (): string | null => {
      const normalizedUserId =
        userId.trim();


      if (
        !normalizedUserId
      ) {
        return '아이디를 입력해주세요.';
      }


      if (
        !phoneVerification
          .phoneVerified
      ) {
        return '전화번호 인증을 완료해주세요.';
      }


      if (
        !phoneVerification
          .verificationToken
      ) {
        return '인증 정보가 없습니다. 다시 인증해주세요.';
      }


      if (
        !newPassword
      ) {
        return '새 비밀번호를 입력해주세요.';
      }


      if (
        newPassword.length <
        8
      ) {
        return '새 비밀번호는 8자 이상 입력해주세요.';
      }


      if (
        !newPasswordConfirm
      ) {
        return '새 비밀번호 확인을 입력해주세요.';
      }


      if (
        newPassword !==
        newPasswordConfirm
      ) {
        return '새 비밀번호가 서로 일치하지 않습니다.';
      }


      return null;
    };


  /**
   * =========================================================
   * 비밀번호 재설정
   * =========================================================
   */
  const handleResetPassword =
    async (): Promise<void> => {
      if (
        resetting
      ) {
        return;
      }


      const validationMessage =
        validatePassword();


      if (
        validationMessage
      ) {
        await modal.alert(
          validationMessage,
        );

        return;
      }


      const verificationToken =
        phoneVerification
          .verificationToken;


      if (
        !verificationToken
      ) {
        await modal.alert(
          '인증 정보가 없습니다. 다시 인증해주세요.',
        );

        return;
      }


      const normalizedPhoneNumber =
        normalizePhoneNumber(
          phoneNumber,
        );


      try {
        setResetting(
          true,
        );


        /**
         * POST /api/v2/auth/password/reset
         *
         * {
         *   userId,
         *   phoneNumber,
         *   verificationToken,
         *   newPassword
         * }
         */
        await resetPassword({
          userId:
            userId.trim(),

          phoneNumber:
            normalizedPhoneNumber,

          verificationToken,

          newPassword,
        });


        setResetCompleted(
          true,
        );

      } catch (
        error: unknown
      ) {
        console.error(
          '비밀번호 재설정 실패:',
          error,
        );


        /**
         * 서버에서 예를 들어
         *
         * "기존 비밀번호와 동일한 비밀번호는 사용할 수 없습니다."
         *
         * 를 반환하면 그대로 사용자에게 표시합니다.
         */
        await modal.alert(
          getErrorMessage(
            error,
            '비밀번호를 변경할 수 없습니다. 입력한 정보를 다시 확인해주세요.',
          ),
        );

      } finally {
        setResetting(
          false,
        );
      }
    };


  /**
   * =========================================================
   * MM:SS
   * =========================================================
   */
  const formatTime = (
    seconds: number,
  ): string => {
    const minutes =
      Math.floor(
        seconds /
          60,
      );


    const remainSeconds =
      seconds %
      60;


    return `${String(
      minutes,
    ).padStart(
      2,
      '0',
    )}:${String(
      remainSeconds,
    ).padStart(
      2,
      '0',
    )}`;
  };


  /**
   * =========================================================
   * 새 비밀번호 확인 상태
   * =========================================================
   */
  const passwordConfirmEntered =
    newPasswordConfirm.length >
    0;


  const passwordMatches =
    passwordConfirmEntered &&
    newPassword ===
      newPasswordConfirm;


  const passwordMismatch =
    passwordConfirmEntered &&
    newPassword !==
      newPasswordConfirm;


  /**
   * =========================================================
   * 변경 버튼 활성화 여부
   * =========================================================
   */
  const canResetPassword =
    phoneVerification
      .phoneVerified &&
    Boolean(
      phoneVerification
        .verificationToken,
    ) &&
    newPassword.length >=
      8 &&
    passwordMatches &&
    !resetting;


  return (
    <main
      className="
        min-h-screen
        bg-gray-50
        px-4
        py-10
      "
    >

      <div
        className="
          mx-auto
          w-full
          max-w-md
        "
      >

        {/* =================================================
            뒤로가기
            ================================================= */}
        <button
          type="button"
          onClick={() =>
            navigate(
              RouteConfig
                .Auth
                .Login
                .fullPath,
            )
          }
          className="
            mb-5
            flex
            items-center
            gap-2
            text-sm
            font-medium
            text-gray-500
            transition-colors
            hover:text-gray-900
          "
        >

          <ArrowLeft
            size={18}
          />

          로그인으로 돌아가기

        </button>


        <section
          className="
            rounded-2xl
            border
            border-gray-200
            bg-white
            p-7
            shadow-sm
          "
        >

          {/* =================================================
              Header
              ================================================= */}
          <header
            className="
              mb-8
              text-center
            "
          >

            <div
              className="
                mx-auto
                mb-4
                flex
                h-14
                w-14
                items-center
                justify-center
                rounded-full
                bg-gray-100
              "
            >

              <KeyRound
                size={26}
                className="text-gray-700"
              />

            </div>


            <h1
              className="
                text-2xl
                font-semibold
                text-gray-900
              "
            >
              비밀번호 찾기
            </h1>


            <p
              className="
                mt-2
                text-sm
                leading-6
                text-gray-500
              "
            >
              아이디와 회원가입 시 등록한
              전화번호를 확인한 후
              비밀번호를 새로 설정합니다.
            </p>

          </header>


          {resetCompleted ? (

            /**
             * =============================================
             * 비밀번호 변경 완료
             * =============================================
             */
            <div>

              <div
                className="
                  rounded-xl
                  border
                  border-emerald-200
                  bg-emerald-50
                  px-5
                  py-7
                  text-center
                "
              >

                <CheckCircle2
                  size={42}
                  className="
                    mx-auto
                    text-emerald-500
                  "
                />


                <h2
                  className="
                    mt-4
                    text-lg
                    font-semibold
                    text-gray-900
                  "
                >
                  비밀번호가 변경되었습니다.
                </h2>


                <p
                  className="
                    mt-2
                    text-sm
                    leading-6
                    text-gray-500
                  "
                >
                  새로운 비밀번호로 로그인해주세요.
                </p>

              </div>


              <button
                type="button"
                onClick={() =>
                  navigate(
                    RouteConfig
                      .Auth
                      .Login
                      .fullPath,
                  )
                }
                className="
                  mt-5
                  min-h-12
                  w-full
                  rounded-xl
                  bg-main
                  px-4
                  py-3
                  text-base
                  font-semibold
                  text-white
                  transition-colors
                  hover:bg-main-medium
                "
              >
                로그인하기
              </button>

            </div>

          ) : (

            /**
             * =============================================
             * 입력 화면
             * =============================================
             */
            <div className="space-y-5">

              {/* =================================================
                  아이디
                  ================================================= */}
              <div>

                <label
                  htmlFor="userId"
                  className="
                    mb-2
                    block
                    text-sm
                    font-semibold
                    text-gray-700
                  "
                >
                  아이디
                </label>


                <input
                  id="userId"
                  type="text"
                  value={
                    userId
                  }
                  disabled={
                    phoneVerification
                      .phoneVerified
                  }
                  onChange={(
                    event,
                  ) =>
                    handleUserIdChange(
                      event.target
                        .value,
                    )
                  }
                  placeholder="아이디를 입력해주세요"
                  autoComplete="username"
                  className="
                    min-h-12
                    w-full
                    rounded-xl
                    border
                    border-gray-300
                    bg-white
                    px-4
                    py-3
                    text-base
                    text-gray-900
                    outline-none
                    transition

                    placeholder:text-gray-400

                    focus:border-main
                    focus:ring-2
                    focus:ring-main/20

                    disabled:cursor-not-allowed
                    disabled:bg-gray-100
                    disabled:text-gray-500
                  "
                />

              </div>


              {/* =================================================
                  전화번호
                  ================================================= */}
              <div>

                <label
                  htmlFor="phoneNumber"
                  className="
                    mb-2
                    block
                    text-sm
                    font-semibold
                    text-gray-700
                  "
                >
                  전화번호
                </label>


                <div className="flex gap-2">

                  <input
                    id="phoneNumber"
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel"
                    value={
                      phoneNumber
                    }
                    onChange={(
                      event,
                    ) =>
                      handlePhoneNumberChange(
                        event.target
                          .value,
                      )
                    }
                    disabled={
                      phoneVerification
                        .phoneVerified
                    }
                    maxLength={11}
                    placeholder="01012345678"
                    className="
                      min-h-12
                      min-w-0
                      flex-1
                      rounded-xl
                      border
                      border-gray-300
                      bg-white
                      px-4
                      py-3
                      text-base
                      text-gray-900
                      outline-none
                      transition

                      placeholder:text-gray-400

                      focus:border-main
                      focus:ring-2
                      focus:ring-main/20

                      disabled:cursor-not-allowed
                      disabled:bg-gray-100
                      disabled:text-gray-500
                    "
                  />


                  <button
                    type="button"
                    onClick={() => {
                      void phoneVerification
                        .sendVerification();
                    }}
                    disabled={
                      !userId.trim() ||
                      phoneVerification
                        .isSendingVerification ||
                      phoneVerification
                        .phoneVerified ||
                      phoneVerification
                        .resendSeconds >
                        0
                    }
                    className="
                      min-h-12
                      shrink-0
                      whitespace-nowrap
                      rounded-xl
                      bg-gray-900
                      px-4
                      py-3
                      text-sm
                      font-semibold
                      text-white
                      transition

                      hover:bg-gray-700

                      disabled:cursor-not-allowed
                      disabled:opacity-50
                    "
                  >

                    {phoneVerification
                      .isSendingVerification
                      ? '발송 중'
                      : phoneVerification
                            .resendSeconds >
                          0
                        ? `${phoneVerification.resendSeconds}초`
                        : phoneVerification
                              .verificationSent
                          ? '재전송'
                          : '인증요청'}

                  </button>

                </div>

              </div>


              {/* =================================================
                  인증번호
                  ================================================= */}
              {phoneVerification
                .verificationSent && (

                <div>

                  <label
                    htmlFor="verificationCode"
                    className="
                      mb-2
                      block
                      text-sm
                      font-semibold
                      text-gray-700
                    "
                  >
                    인증번호
                  </label>


                  {phoneVerification
                    .phoneVerified ? (

                    <div
                      className="
                        flex
                        min-h-12
                        items-center
                        gap-2
                        rounded-xl
                        border
                        border-emerald-200
                        bg-emerald-50
                        px-4
                        py-3
                        text-sm
                        font-semibold
                        text-emerald-700
                      "
                    >

                      <CheckCircle2
                        size={18}
                      />

                      휴대폰 인증이 완료되었습니다.

                    </div>

                  ) : (

                    <>

                      <div className="relative">

                        <input
                          id="verificationCode"
                          type="text"
                          inputMode="numeric"
                          value={
                            phoneVerification
                              .verificationCode
                          }
                          onChange={(
                            event,
                          ) =>
                            phoneVerification
                              .updateVerificationCode(
                                event
                                  .target
                                  .value,
                              )
                          }
                          maxLength={6}
                          placeholder="6자리 인증번호"
                          className="
                            min-h-12
                            w-full
                            rounded-xl
                            border
                            border-gray-300
                            bg-white
                            px-4
                            py-3
                            pr-20
                            text-base
                            text-gray-900
                            outline-none
                            transition

                            placeholder:text-gray-400

                            focus:border-main
                            focus:ring-2
                            focus:ring-main/20
                          "
                        />


                        <span
                          className="
                            absolute
                            right-4
                            top-1/2
                            -translate-y-1/2
                            text-sm
                            font-semibold
                            text-red-500
                          "
                        >
                          {formatTime(
                            phoneVerification
                              .verificationSeconds,
                          )}
                        </span>

                      </div>


                      <button
                        type="button"
                        onClick={() => {
                          void phoneVerification
                            .verifyCode();
                        }}
                        disabled={
                          phoneVerification
                            .isVerifyingCode ||
                          phoneVerification
                            .verificationSeconds <=
                            0
                        }
                        className="
                          mt-3
                          min-h-12
                          w-full
                          rounded-xl
                          border
                          border-gray-300
                          bg-white
                          px-4
                          py-3
                          text-sm
                          font-semibold
                          text-gray-700
                          transition

                          hover:bg-gray-50

                          disabled:cursor-not-allowed
                          disabled:opacity-50
                        "
                      >

                        {phoneVerification
                          .isVerifyingCode
                          ? '확인 중...'
                          : '인증번호 확인'}

                      </button>

                    </>

                  )}

                </div>

              )}


              {/* =================================================
                  인증 메시지
                  ================================================= */}
              {phoneVerification
                .message && (

                <div
                  className={`
                    rounded-lg
                    px-4
                    py-3
                    text-sm

                    ${
                      phoneVerification
                        .phoneVerified
                        ? 'bg-emerald-50 text-emerald-700'
                        : 'bg-gray-50 text-gray-600'
                    }
                  `}
                >
                  {
                    phoneVerification
                      .message
                  }
                </div>

              )}


              {/* =================================================
                  인증 완료 후 새 비밀번호
                  ================================================= */}
              {phoneVerification
                .phoneVerified && (

                <>

                  {/* 새 비밀번호 */}
                  <div>

                    <label
                      htmlFor="newPassword"
                      className="
                        mb-2
                        block
                        text-sm
                        font-semibold
                        text-gray-700
                      "
                    >
                      새 비밀번호
                    </label>


                    <input
                      id="newPassword"
                      type="password"
                      value={
                        newPassword
                      }
                      onChange={(
                        event,
                      ) =>
                        setNewPassword(
                          event.target
                            .value,
                        )
                      }
                      placeholder="새 비밀번호를 입력해주세요"
                      autoComplete="new-password"
                      className="
                        min-h-12
                        w-full
                        rounded-xl
                        border
                        border-gray-300
                        bg-white
                        px-4
                        py-3
                        text-base
                        text-gray-900
                        outline-none
                        transition

                        placeholder:text-gray-400

                        focus:border-main
                        focus:ring-2
                        focus:ring-main/20
                      "
                    />


                    <p className="mt-1.5 text-xs text-gray-400">
                      8자 이상 입력해주세요. 기존 비밀번호와 동일한 비밀번호는 사용할 수 없습니다.
                    </p>

                  </div>


                  {/* 새 비밀번호 확인 */}
                  <div>

                    <label
                      htmlFor="newPasswordConfirm"
                      className="
                        mb-2
                        block
                        text-sm
                        font-semibold
                        text-gray-700
                      "
                    >
                      새 비밀번호 확인
                    </label>


                    <input
                      id="newPasswordConfirm"
                      type="password"
                      value={
                        newPasswordConfirm
                      }
                      onChange={(
                        event,
                      ) =>
                        setNewPasswordConfirm(
                          event.target
                            .value,
                        )
                      }
                      placeholder="새 비밀번호를 다시 입력해주세요"
                      autoComplete="new-password"
                      className={`
                        min-h-12
                        w-full
                        rounded-xl
                        border
                        bg-white
                        px-4
                        py-3
                        text-base
                        text-gray-900
                        outline-none
                        transition

                        placeholder:text-gray-400

                        focus:ring-2
                        focus:ring-main/20

                        ${
                          passwordMismatch
                            ? `
                              border-red-400
                              focus:border-red-400
                            `
                            : passwordMatches
                              ? `
                                border-emerald-400
                                focus:border-emerald-400
                              `
                              : `
                                border-gray-300
                                focus:border-main
                              `
                        }
                      `}
                    />

                  </div>


                  {/* 비밀번호 일치 여부 */}
                  {passwordConfirmEntered && (

                    <p
                      className={`
                        flex
                        items-center
                        gap-1.5
                        text-xs
                        font-medium

                        ${
                          passwordMatches
                            ? 'text-emerald-600'
                            : 'text-red-500'
                        }
                      `}
                    >

                      {passwordMatches && (
                        <CheckCircle2
                          size={14}
                        />
                      )}


                      {passwordMatches
                        ? '비밀번호가 일치합니다.'
                        : '비밀번호가 일치하지 않습니다.'}

                    </p>

                  )}


                  {/* 비밀번호 변경 */}
                  <button
                    type="button"
                    onClick={() => {
                      void handleResetPassword();
                    }}
                    disabled={
                      !canResetPassword
                    }
                    className="
                      flex
                      min-h-12
                      w-full
                      items-center
                      justify-center
                      rounded-xl
                      bg-main
                      px-4
                      py-3
                      text-base
                      font-semibold
                      text-white
                      transition

                      hover:bg-main-medium

                      disabled:cursor-not-allowed
                      disabled:opacity-50
                    "
                  >

                    {resetting
                      ? '변경 중...'
                      : '비밀번호 변경'}

                  </button>

                </>

              )}

            </div>

          )}

        </section>

      </div>

    </main>
  );
};


export default ResetPasswordPage;